import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, DashaPeriod, PlanetName } from '../types/kundali';
import { 
  Sparkles, 
  Clock, 
  ChevronDown, 
  ChevronRight, 
  Activity, 
  CheckCircle2, 
  Calendar, 
  Zap, 
  Target, 
  Shield, 
  AlertTriangle, 
  ArrowRight, 
  Layers, 
  Compass,
  ArrowLeftRight,
  TrendingUp,
  FileText
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { UI_TEXT } from '../utils/language';
import { ShaniSadeSatiTimeline } from './ShaniSadeSatiTimeline';
import { 
  calculatePratyantardashas, 
  calculateSookshmaDashas, 
  calculatePranaDashas, 
  getMdAdRelationship,
  PratyantarDashaItem,
  SookshmaDashaItem,
  PranaDashaItem
} from '../utils/dashaCalculator';
import { CockpitHeader } from './v600/CockpitHeader';
import { DualChartContextPanel } from './v600/DualChartContextPanel';
import { GocharQuickGlance } from './v600/GocharQuickGlance';
import { EvidenceProvenanceDrawer } from './v600/EvidenceProvenanceDrawer';
import { BtrDrawer } from './BtrDrawer';

export interface DashaProgressInfo {
  percentage: number;
  elapsedFormatted: string;
  remainingFormatted: string;
  totalFormatted: string;
  totalDays: number;
  elapsedDays: number;
  remainingDays: number;
  isCompleted: boolean;
  isUpcoming: boolean;
  isActive: boolean;
}

/**
 * Format a fractional duration in years into clean human units (e.g., "2y 4m 10d", "3m 15d", or "12d")
 */
export function formatHumanDurationFromYears(durationYears: number): string {
  if (!durationYears || durationYears <= 0) return '0d';
  const totalDays = Math.round(durationYears * 365.25);
  const years = Math.floor(totalDays / 365.25);
  const remDaysAfterYears = totalDays - Math.floor(years * 365.25);
  const months = Math.floor(remDaysAfterYears / 30.4375);
  const days = Math.max(0, Math.floor(remDaysAfterYears - Math.floor(months * 30.4375)));

  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0 || parts.length === 0) parts.push(`${days}d`);
  return parts.join(' ');
}

/**
 * Calculate exact elapsed and remaining time and percentage for a dasha period
 */
export function calculateDashaProgress(startDateStr: string, endDateStr: string, customNow?: Date): DashaProgressInfo {
  const start = new Date(startDateStr).getTime();
  const end = new Date(endDateStr).getTime();
  const now = customNow ? customNow.getTime() : new Date().getTime();
  const totalMs = Math.max(1, end - start);
  const totalDays = Math.round(totalMs / (1000 * 60 * 60 * 24));

  const formatHumanDuration = (days: number) => {
    if (days <= 0) return '0d';
    const years = Math.floor(days / 365.25);
    const remDaysAfterYears = days - Math.floor(years * 365.25);
    const months = Math.floor(remDaysAfterYears / 30.4375);
    const remainingDays = Math.max(0, Math.floor(remDaysAfterYears - Math.floor(months * 30.4375)));

    const parts: string[] = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}m`);
    if (remainingDays > 0 || parts.length === 0) parts.push(`${remainingDays}d`);
    return parts.join(' ');
  };

  if (now <= start) {
    return {
      percentage: 0,
      elapsedFormatted: '0d',
      remainingFormatted: formatHumanDuration(totalDays),
      totalFormatted: formatHumanDuration(totalDays),
      totalDays,
      elapsedDays: 0,
      remainingDays: totalDays,
      isCompleted: false,
      isUpcoming: true,
      isActive: false
    };
  }

  if (now >= end) {
    return {
      percentage: 100,
      elapsedFormatted: formatHumanDuration(totalDays),
      remainingFormatted: '0d',
      totalFormatted: formatHumanDuration(totalDays),
      totalDays,
      elapsedDays: totalDays,
      remainingDays: 0,
      isCompleted: true,
      isUpcoming: false,
      isActive: false
    };
  }

  const elapsedMs = now - start;
  const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
  const remainingDays = Math.max(0, totalDays - elapsedDays);
  const rawPct = (elapsedMs / totalMs) * 100;
  const percentage = Math.min(99.9, Math.max(0.1, Math.round(rawPct * 10) / 10));

  return {
    percentage,
    elapsedFormatted: formatHumanDuration(elapsedDays),
    remainingFormatted: formatHumanDuration(remainingDays),
    totalFormatted: formatHumanDuration(totalDays),
    totalDays,
    elapsedDays,
    remainingDays,
    isCompleted: false,
    isUpcoming: false,
    isActive: true
  };
}

interface DashaTimelineProps {
  kundali: CompleteKundali;
  lang?: Language;
  targetAnalysisDateTime?: Date;
  onTargetAnalysisDateTimeChange?: (date: Date) => void;
  onNavigateToGochar?: () => void;
  onOpenBtrDrawer?: () => void;
}

export const DashaTimeline: React.FC<DashaTimelineProps> = ({
  kundali,
  lang = 'en',
  targetAnalysisDateTime: externalTargetMoment,
  onTargetAnalysisDateTimeChange: externalOnTargetMomentChange,
  onNavigateToGochar,
  onOpenBtrDrawer
}) => {
  const isHi = lang === 'hi';
  const { dashas, houses, planets } = kundali;

  // Internal moment state if external not provided
  const [internalMoment, setInternalMoment] = useState<Date>(() => new Date());
  const targetMoment = externalTargetMoment || internalMoment;
  const handleMomentChange = (newDate: Date) => {
    if (externalOnTargetMomentChange) {
      externalOnTargetMomentChange(newDate);
    } else {
      setInternalMoment(newDate);
    }
  };

  // State for level depth tabs
  const [activeLevel, setActiveLevel] = useState<'MD' | 'AD' | 'PD' | 'SD' | 'PrD'>('MD');
  // Cross-highlighting state
  const [highlightedPlanet, setHighlightedPlanet] = useState<string | null>(null);
  // Expandable periods state
  const [expandedPeriodKeys, setExpandedPeriodKeys] = useState<Set<string>>(new Set());
  // BTR Drawer local trigger
  const [isBtrDrawerOpen, setIsBtrDrawerOpen] = useState<boolean>(false);
  // Mobile active subview
  const [mobileTab, setMobileTab] = useState<'timeline' | 'charts' | 'gochar' | 'evidence'>('timeline');

  const togglePeriodExpanded = (key: string) => {
    setExpandedPeriodKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Authoritative House Involvement Model
  const getHouseInvolvement = (planetName: string) => {
    const owned = houses.filter(h => h.signLord === planetName).map(h => h.houseNumber);
    const placed = planets.find(p => p.name === planetName)?.house || null;
    
    const aspected: number[] = [];
    if (placed) {
      aspected.push(((placed - 1 + 6) % 12) + 1); // 7th aspect
      if (planetName === 'Mars') {
        aspected.push(((placed - 1 + 3) % 12) + 1); // 4th
        aspected.push(((placed - 1 + 7) % 12) + 1); // 8th
      } else if (planetName === 'Jupiter' || planetName === 'Rahu' || planetName === 'Ketu') {
        aspected.push(((placed - 1 + 4) % 12) + 1); // 5th
        aspected.push(((placed - 1 + 8) % 12) + 1); // 9th
      } else if (planetName === 'Saturn') {
        aspected.push(((placed - 1 + 2) % 12) + 1); // 3rd
        aspected.push(((placed - 1 + 9) % 12) + 1); // 10th
      }
    }
    const uniqueAspected = Array.from(new Set(aspected)).sort((a, b) => a - b);

    const parts: string[] = [];
    if (owned.length > 0) parts.push(`Owns: H${owned.join(', H')}`);
    if (placed) parts.push(`In: H${placed}`);
    if (uniqueAspected.length > 0) parts.push(`Drishti: H${uniqueAspected.join(', H')}`);

    return {
      owned,
      placed,
      aspected: uniqueAspected,
      displayLabel: parts.join(' • ') || (placed ? `In: H${placed}` : 'Unaspected')
    };
  };

  // Determine active MD at targetMoment
  const { currentMD, mdIndex, prevMD, nextMD } = useMemo(() => {
    const refMs = targetMoment.getTime();
    let idx = dashas.findIndex(d => {
      const s = new Date(d.startDate).getTime();
      const e = new Date(d.endDate).getTime();
      return s <= refMs && e >= refMs;
    });

    if (idx === -1 && dashas.length > 0) {
      idx = 0;
    }

    return {
      currentMD: idx >= 0 ? dashas[idx] : null,
      mdIndex: idx,
      prevMD: idx > 0 ? dashas[idx - 1] : null,
      nextMD: idx < dashas.length - 1 ? dashas[idx + 1] : null
    };
  }, [dashas, targetMoment]);

  // Determine active AD inside currentMD at targetMoment
  const { currentAD, adIndex, prevAD, nextAD } = useMemo(() => {
    if (!currentMD || !currentMD.antardashas) {
      return { currentAD: null, adIndex: -1, prevAD: null, nextAD: null };
    }
    const refMs = targetMoment.getTime();
    let idx = currentMD.antardashas.findIndex(ad => {
      const s = new Date(ad.startDate).getTime();
      const e = new Date(ad.endDate).getTime();
      return s <= refMs && e >= refMs;
    });

    if (idx === -1 && currentMD.antardashas.length > 0) {
      idx = 0;
    }

    return {
      currentAD: idx >= 0 ? currentMD.antardashas[idx] : null,
      adIndex: idx,
      prevAD: idx > 0 ? currentMD.antardashas[idx - 1] : null,
      nextAD: idx < currentMD.antardashas.length - 1 ? currentMD.antardashas[idx + 1] : null
    };
  }, [currentMD, targetMoment]);

  // Calculate Pratyantardashas inside active AD
  const allPDs = useMemo(() => {
    if (!currentAD) return [];
    return calculatePratyantardashas(currentAD.planet, currentAD.startDate, currentAD.endDate);
  }, [currentAD]);

  const { currentPD, pdIndex, prevPD, nextPD } = useMemo(() => {
    const refMs = targetMoment.getTime();
    const idx = allPDs.findIndex(pd => {
      const s = new Date(pd.startDate).getTime();
      const e = new Date(pd.endDate).getTime();
      return s <= refMs && e >= refMs;
    });
    return {
      currentPD: idx >= 0 ? allPDs[idx] : (allPDs[0] || null),
      pdIndex: idx,
      prevPD: idx > 0 ? allPDs[idx - 1] : null,
      nextPD: idx < allPDs.length - 1 ? allPDs[idx + 1] : null
    };
  }, [allPDs, targetMoment]);

  // Calculate Sookshma inside active PD
  const allSDs = useMemo(() => {
    if (!currentPD) return [];
    return calculateSookshmaDashas(currentPD.planet, currentPD.startDate, currentPD.endDate);
  }, [currentPD]);

  const { currentSD, sdIndex, prevSD, nextSD } = useMemo(() => {
    const refMs = targetMoment.getTime();
    const idx = allSDs.findIndex(sd => {
      const s = new Date(sd.startDate).getTime();
      const e = new Date(sd.endDate).getTime();
      return s <= refMs && e >= refMs;
    });
    return {
      currentSD: idx >= 0 ? allSDs[idx] : (allSDs[0] || null),
      sdIndex: idx,
      prevSD: idx > 0 ? allSDs[idx - 1] : null,
      nextSD: idx < allSDs.length - 1 ? allSDs[idx + 1] : null
    };
  }, [allSDs, targetMoment]);

  // Calculate Prana inside active SD
  const allPrDs = useMemo(() => {
    if (!currentSD) return [];
    return calculatePranaDashas(currentSD.planet, currentSD.startDate, currentSD.endDate);
  }, [currentSD]);

  const { currentPrD, prdIndex, prevPrD, nextPrD } = useMemo(() => {
    const refMs = targetMoment.getTime();
    const idx = allPrDs.findIndex(prd => {
      const s = new Date(prd.startDate).getTime();
      const e = new Date(prd.endDate).getTime();
      return s <= refMs && e >= refMs;
    });
    return {
      currentPrD: idx >= 0 ? allPrDs[idx] : (allPrDs[0] || null),
      prdIndex: idx,
      prevPrD: idx > 0 ? allPrDs[idx - 1] : null,
      nextPrD: idx < allPrDs.length - 1 ? allPrDs[idx + 1] : null
    };
  }, [allPrDs, targetMoment]);

  // Verified Natal MD-AD positional relationship
  const mdAdRelation = useMemo(() => {
    if (!currentMD || !currentAD) return null;
    return getMdAdRelationship(currentMD.planet, currentAD.planet, planets);
  }, [currentMD, currentAD, planets]);

  // Progress for active periods
  const mdProgress = useMemo(() => {
    if (!currentMD) return null;
    return calculateDashaProgress(currentMD.startDate, currentMD.endDate, targetMoment);
  }, [currentMD, targetMoment]);

  const adProgress = useMemo(() => {
    if (!currentAD) return null;
    return calculateDashaProgress(currentAD.startDate, currentAD.endDate, targetMoment);
  }, [currentAD, targetMoment]);

  const pdProgress = useMemo(() => {
    if (!currentPD) return null;
    return calculateDashaProgress(currentPD.startDate, currentPD.endDate, targetMoment);
  }, [currentPD, targetMoment]);

  // Explicit period date setter helper
  const setAnalysisToPeriod = (dateStr: string) => {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      handleMomentChange(d);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 py-3">
      {/* Top Cockpit Bar: Profile Context & Shared Moment Controller */}
      <CockpitHeader
        kundali={kundali}
        lang={lang}
        targetAnalysisDateTime={targetMoment}
        onTargetAnalysisDateTimeChange={handleMomentChange}
        onOpenBtrDrawer={() => {
          if (onOpenBtrDrawer) onOpenBtrDrawer();
          else setIsBtrDrawerOpen(true);
        }}
      />

      {/* Mobile view switcher */}
      <div className="lg:hidden flex items-center justify-between bg-white border border-[#B58A4A]/30 rounded-xl p-1 mb-4 shadow-2xs">
        <button
          onClick={() => setMobileTab('timeline')}
          className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
            mobileTab === 'timeline' ? 'bg-[#174C4B] text-white' : 'text-stone-600 hover:text-stone-900'
          }`}
        >
          {isHi ? 'दशा समयरेखा' : 'Dasha'}
        </button>
        <button
          onClick={() => setMobileTab('charts')}
          className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
            mobileTab === 'charts' ? 'bg-[#174C4B] text-white' : 'text-stone-600 hover:text-stone-900'
          }`}
        >
          {isHi ? 'कुण्डली' : 'Charts'}
        </button>
        <button
          onClick={() => setMobileTab('gochar')}
          className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
            mobileTab === 'gochar' ? 'bg-[#174C4B] text-white' : 'text-stone-600 hover:text-stone-900'
          }`}
        >
          {isHi ? 'गोचर' : 'Gochar'}
        </button>
        <button
          onClick={() => setMobileTab('evidence')}
          className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
            mobileTab === 'evidence' ? 'bg-[#174C4B] text-white' : 'text-stone-600 hover:text-stone-900'
          }`}
        >
          {isHi ? 'प्रमाण' : 'Evidence'}
        </button>
      </div>

      {/* Main Grid: Left Primary Analytical Area (~60%) & Right Context Area (~40%) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* LEFT COLUMN: Executive Summary, Vimshottari Timeline, Evidence Drawer (~60% / col-span-7) */}
        <div className={`lg:col-span-7 space-y-5 ${mobileTab !== 'timeline' && mobileTab !== 'evidence' ? 'hidden lg:block' : ''}`}>
          {/* 1. Compact Executive Summary Card */}
          <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between pb-3 border-b border-[#B58A4A]/20">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#B58A4A]" />
                <h2 className="font-serif font-bold text-sm text-[#174C4B] uppercase tracking-wider">
                  {isHi ? 'सक्रिय दशा सारांश (Executive Summary)' : 'Active Dasha Executive Summary'}
                </h2>
              </div>
              {mdAdRelation && (
                <span className={`text-2xs font-semibold px-2.5 py-0.5 rounded-full border ${
                  mdAdRelation.nature === 'harmonious'
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                    : mdAdRelation.nature === 'frictional'
                    ? 'bg-amber-50 text-amber-800 border-amber-300'
                    : 'bg-stone-100 text-stone-700 border-stone-300'
                }`}>
                  MD–AD: {mdAdRelation.labelEn}
                </span>
              )}
            </div>

            {/* Active Hierarchy Triple Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-3">
              {/* Mahadasha (MD) */}
              <div 
                onClick={() => {
                  if (currentMD) setHighlightedPlanet(currentMD.planet);
                }}
                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                  highlightedPlanet === currentMD?.planet
                    ? 'bg-amber-50/80 border-[#B58A4A] ring-1 ring-[#B58A4A]'
                    : 'bg-white border-stone-200 hover:border-[#174C4B]/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-3xs font-bold text-stone-500 uppercase">Mahadasha (MD)</span>
                  <span className="text-2xs font-mono font-bold text-[#174C4B]">{mdProgress?.percentage}%</span>
                </div>
                <div className="font-serif font-bold text-base text-[#174C4B] flex items-center gap-1.5">
                  <span>{currentMD?.planet}</span>
                </div>
                <div className="text-3xs text-stone-600 mt-1 space-y-0.5">
                  <div className="truncate font-medium">{currentMD ? getHouseInvolvement(currentMD.planet).displayLabel : ''}</div>
                  <div className="text-stone-400 font-mono">{currentMD ? `${formatDate(currentMD.startDate)} → ${formatDate(currentMD.endDate)}` : ''}</div>
                </div>
                {/* Slim Non-Dominant Progress */}
                <div className="w-full bg-stone-100 rounded-full h-1 mt-2 overflow-hidden">
                  <div 
                    className="bg-[#174C4B] h-1 rounded-full transition-all" 
                    style={{ width: `${mdProgress?.percentage || 0}%` }}
                  />
                </div>
              </div>

              {/* Antardasha (AD) */}
              <div 
                onClick={() => {
                  if (currentAD) setHighlightedPlanet(currentAD.planet);
                }}
                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                  highlightedPlanet === currentAD?.planet
                    ? 'bg-amber-50/80 border-[#B58A4A] ring-1 ring-[#B58A4A]'
                    : 'bg-white border-stone-200 hover:border-[#174C4B]/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-3xs font-bold text-stone-500 uppercase">Antardasha (AD)</span>
                  <span className="text-2xs font-mono font-bold text-[#174C4B]">{adProgress?.percentage}%</span>
                </div>
                <div className="font-serif font-bold text-base text-[#174C4B] flex items-center gap-1.5">
                  <span>{currentAD?.planet}</span>
                </div>
                <div className="text-3xs text-stone-600 mt-1 space-y-0.5">
                  <div className="truncate font-medium">{currentAD ? getHouseInvolvement(currentAD.planet).displayLabel : ''}</div>
                  <div className="text-stone-400 font-mono">{currentAD ? `${formatDate(currentAD.startDate)} → ${formatDate(currentAD.endDate)}` : ''}</div>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-1 mt-2 overflow-hidden">
                  <div 
                    className="bg-[#B58A4A] h-1 rounded-full transition-all" 
                    style={{ width: `${adProgress?.percentage || 0}%` }}
                  />
                </div>
              </div>

              {/* Pratyantardasha (PD) */}
              <div 
                onClick={() => {
                  if (currentPD) setHighlightedPlanet(currentPD.planet);
                }}
                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                  highlightedPlanet === currentPD?.planet
                    ? 'bg-amber-50/80 border-[#B58A4A] ring-1 ring-[#B58A4A]'
                    : 'bg-white border-stone-200 hover:border-[#174C4B]/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-3xs font-bold text-stone-500 uppercase">Pratyantar (PD)</span>
                  <span className="text-2xs font-mono font-bold text-[#174C4B]">{pdProgress?.percentage}%</span>
                </div>
                <div className="font-serif font-bold text-base text-[#174C4B] flex items-center gap-1.5">
                  <span>{currentPD?.planet}</span>
                </div>
                <div className="text-3xs text-stone-600 mt-1 space-y-0.5">
                  <div className="truncate font-medium">{currentPD ? getHouseInvolvement(currentPD.planet).displayLabel : ''}</div>
                  <div className="text-stone-400 font-mono">{currentPD ? `${formatDate(currentPD.startDate)} → ${formatDate(currentPD.endDate)}` : ''}</div>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-1 mt-2 overflow-hidden">
                  <div 
                    className="bg-emerald-600 h-1 rounded-full transition-all" 
                    style={{ width: `${pdProgress?.percentage || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 2. Vimshottari Hierarchical Timeline & Navigation */}
          <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl shadow-sm p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-[#B58A4A]/20">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-[#B58A4A]" />
                <h3 className="font-serif font-bold text-sm text-[#174C4B] tracking-wide">
                  {isHi ? 'विंशोत्तरी दशा स्तर (Vimshottari Level Depth)' : 'Vimshottari Level Depth'}
                </h3>
              </div>

              {/* Level Tabs */}
              <div className="flex items-center bg-stone-100 border border-stone-200 rounded-lg p-0.5 text-xs font-semibold">
                <button
                  onClick={() => setActiveLevel('MD')}
                  className={`px-2.5 py-1 rounded-md transition-all ${
                    activeLevel === 'MD' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  MD
                </button>
                <button
                  onClick={() => setActiveLevel('AD')}
                  className={`px-2.5 py-1 rounded-md transition-all ${
                    activeLevel === 'AD' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  AD
                </button>
                <button
                  onClick={() => setActiveLevel('PD')}
                  className={`px-2.5 py-1 rounded-md transition-all ${
                    activeLevel === 'PD' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  PD
                </button>
                <button
                  onClick={() => setActiveLevel('SD')}
                  className={`px-2.5 py-1 rounded-md transition-all ${
                    activeLevel === 'SD' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  SD
                </button>
                <button
                  onClick={() => setActiveLevel('PrD')}
                  className={`px-2.5 py-1 rounded-md transition-all ${
                    activeLevel === 'PrD' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  PrD
                </button>
              </div>
            </div>

            {/* PREVIOUS / CURRENT / NEXT Triple Card Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 my-4">
              {/* PREVIOUS */}
              <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-2.5 text-2xs">
                <span className="text-3xs font-bold text-stone-400 uppercase block mb-1">PREVIOUS</span>
                {activeLevel === 'MD' && prevMD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{prevMD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(prevMD.startDate)} → {formatDate(prevMD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(prevMD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'AD' && prevAD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{prevAD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(prevAD.startDate)} → {formatDate(prevAD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(prevAD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'PD' && prevPD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{prevPD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(prevPD.startDate)} → {formatDate(prevPD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(prevPD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'SD' && prevSD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{prevSD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(prevSD.startDate)} → {formatDate(prevSD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(prevSD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'PrD' && prevPrD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{prevPrD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(prevPrD.startDate)} → {formatDate(prevPrD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(prevPrD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {((activeLevel === 'MD' && !prevMD) || (activeLevel === 'AD' && !prevAD) || (activeLevel === 'PD' && !prevPD)) && (
                  <span className="text-stone-400 italic">None (First Period)</span>
                )}
              </div>

              {/* CURRENT */}
              <div className="bg-amber-50/60 border border-[#B58A4A]/50 rounded-xl p-2.5 text-2xs shadow-2xs">
                <span className="text-3xs font-bold text-[#8E692B] uppercase block mb-1">CURRENT ACTIVE</span>
                {activeLevel === 'MD' && currentMD && (
                  <div>
                    <div className="font-bold text-sm text-[#174C4B]">{currentMD.planet}</div>
                    <div className="text-stone-600 font-mono text-3xs mt-0.5">{formatDate(currentMD.startDate)} → {formatDate(currentMD.endDate)}</div>
                    <div className="text-3xs text-stone-600 mt-1 font-medium">{getHouseInvolvement(currentMD.planet).displayLabel}</div>
                  </div>
                )}
                {activeLevel === 'AD' && currentAD && (
                  <div>
                    <div className="font-bold text-sm text-[#174C4B]">{currentAD.planet}</div>
                    <div className="text-stone-600 font-mono text-3xs mt-0.5">{formatDate(currentAD.startDate)} → {formatDate(currentAD.endDate)}</div>
                    <div className="text-3xs text-stone-600 mt-1 font-medium">{getHouseInvolvement(currentAD.planet).displayLabel}</div>
                  </div>
                )}
                {activeLevel === 'PD' && currentPD && (
                  <div>
                    <div className="font-bold text-sm text-[#174C4B]">{currentPD.planet}</div>
                    <div className="text-stone-600 font-mono text-3xs mt-0.5">{formatDate(currentPD.startDate)} → {formatDate(currentPD.endDate)}</div>
                    <div className="text-3xs text-stone-600 mt-1 font-medium">{getHouseInvolvement(currentPD.planet).displayLabel}</div>
                  </div>
                )}
                {activeLevel === 'SD' && currentSD && (
                  <div>
                    <div className="font-bold text-sm text-[#174C4B]">{currentSD.planet}</div>
                    <div className="text-stone-600 font-mono text-3xs mt-0.5">{formatDate(currentSD.startDate)} → {formatDate(currentSD.endDate)}</div>
                  </div>
                )}
                {activeLevel === 'PrD' && currentPrD && (
                  <div>
                    <div className="font-bold text-sm text-[#174C4B]">{currentPrD.planet}</div>
                    <div className="text-stone-600 font-mono text-3xs mt-0.5">{formatDate(currentPrD.startDate)} → {formatDate(currentPrD.endDate)}</div>
                  </div>
                )}
              </div>

              {/* NEXT */}
              <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-2.5 text-2xs">
                <span className="text-3xs font-bold text-stone-400 uppercase block mb-1">NEXT UPCOMING</span>
                {activeLevel === 'MD' && nextMD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{nextMD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(nextMD.startDate)} → {formatDate(nextMD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(nextMD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'AD' && nextAD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{nextAD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(nextAD.startDate)} → {formatDate(nextAD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(nextAD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'PD' && nextPD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{nextPD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(nextPD.startDate)} → {formatDate(nextPD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(nextPD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'SD' && nextSD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{nextSD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(nextSD.startDate)} → {formatDate(nextSD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(nextSD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {activeLevel === 'PrD' && nextPrD && (
                  <div>
                    <div className="font-bold text-[#174C4B]">{nextPrD.planet}</div>
                    <div className="text-stone-500 font-mono text-3xs mt-0.5">{formatDate(nextPrD.startDate)} → {formatDate(nextPrD.endDate)}</div>
                    <button
                      onClick={() => setAnalysisToPeriod(nextPrD.startDate)}
                      className="mt-2 text-3xs font-semibold text-[#174C4B] hover:text-[#B58A4A] flex items-center gap-1"
                    >
                      <span>Analyse Start</span>
                      <ArrowRight className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
                {((activeLevel === 'MD' && !nextMD) || (activeLevel === 'AD' && !nextAD) || (activeLevel === 'PD' && !nextPD)) && (
                  <span className="text-stone-400 italic">None (Last Period)</span>
                )}
              </div>
            </div>

            {/* List / Tree View for all periods in active tier */}
            <div className="space-y-2 mt-4 max-h-[420px] overflow-y-auto pr-1">
              {activeLevel === 'MD' && dashas.map(d => {
                const isExpanded = expandedPeriodKeys.has(d.planet);
                const isCurrent = d.planet === currentMD?.planet;
                const involvement = getHouseInvolvement(d.planet);
                return (
                  <div 
                    key={d.planet}
                    className={`rounded-xl border transition-all ${
                      isCurrent
                        ? 'bg-amber-50/40 border-[#B58A4A] ring-1 ring-[#B58A4A]/30'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div 
                      onClick={() => togglePeriodExpanded(d.planet)}
                      className="p-3 flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[#174C4B]/10 flex items-center justify-center font-bold text-xs text-[#174C4B]">
                          {d.planet.slice(0, 2)}
                        </div>
                        <div>
                          <div className="font-bold text-xs text-[#174C4B] flex items-center gap-2">
                            <span>{d.planet} Mahadasha</span>
                            {isCurrent && (
                              <span className="text-3xs font-bold px-1.5 py-0.5 rounded bg-[#174C4B] text-white">ACTIVE</span>
                            )}
                          </div>
                          <div className="text-3xs text-stone-500 font-mono mt-0.5">
                            {formatDate(d.startDate)} → {formatDate(d.endDate)} ({formatHumanDurationFromYears(d.durationYears)})
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-right text-3xs text-stone-600 font-medium">
                          {involvement.displayLabel}
                        </div>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                      </div>
                    </div>

                    {/* Expandable Action Bar & Details */}
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-stone-100 bg-stone-50/50 rounded-b-xl space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-2xs">
                          <div className="flex items-center gap-1 text-stone-600">
                            <Clock className="w-3.5 h-3.5 text-[#B58A4A]" />
                            <span className="font-semibold text-[#174C4B]">Analyse Period At:</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => setAnalysisToPeriod(d.startDate)}
                              className="px-2 py-1 rounded bg-white hover:bg-[#174C4B] text-stone-700 hover:text-white border border-stone-200 text-3xs font-semibold transition-colors shadow-2xs"
                            >
                              Period Start ({formatDate(d.startDate)})
                            </button>
                            <button
                              onClick={() => setAnalysisToPeriod(d.endDate)}
                              className="px-2 py-1 rounded bg-white hover:bg-[#174C4B] text-stone-700 hover:text-white border border-stone-200 text-3xs font-semibold transition-colors shadow-2xs"
                            >
                              Period End ({formatDate(d.endDate)})
                            </button>
                            <button
                              onClick={() => handleMomentChange(new Date())}
                              className="px-2 py-1 rounded bg-[#B58A4A]/20 hover:bg-[#B58A4A]/40 text-[#8E692B] border border-[#B58A4A]/40 text-3xs font-bold transition-colors shadow-2xs"
                            >
                              Now / Today
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {activeLevel === 'AD' && currentMD?.antardashas?.map(ad => {
                const isExpanded = expandedPeriodKeys.has(`AD-${ad.planet}`);
                const isCurrent = ad.planet === currentAD?.planet;
                const involvement = getHouseInvolvement(ad.planet);
                return (
                  <div 
                    key={ad.planet}
                    className={`rounded-xl border transition-all ${
                      isCurrent
                        ? 'bg-amber-50/40 border-[#B58A4A] ring-1 ring-[#B58A4A]/30'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div 
                      onClick={() => togglePeriodExpanded(`AD-${ad.planet}`)}
                      className="p-3 flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[#B58A4A]/15 flex items-center justify-center font-bold text-xs text-[#8E692B]">
                          {ad.planet.slice(0, 2)}
                        </div>
                        <div>
                          <div className="font-bold text-xs text-[#174C4B] flex items-center gap-2">
                            <span>{ad.planet} Antardasha</span>
                            {isCurrent && (
                              <span className="text-3xs font-bold px-1.5 py-0.5 rounded bg-[#174C4B] text-white">ACTIVE</span>
                            )}
                          </div>
                          <div className="text-3xs text-stone-500 font-mono mt-0.5">
                            {formatDate(ad.startDate)} → {formatDate(ad.endDate)} ({formatHumanDurationFromYears(ad.durationYears)})
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-right text-3xs text-stone-600 font-medium">
                          {involvement.displayLabel}
                        </div>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-stone-100 bg-stone-50/50 rounded-b-xl space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-2xs">
                          <div className="flex items-center gap-1 text-stone-600">
                            <Clock className="w-3.5 h-3.5 text-[#B58A4A]" />
                            <span className="font-semibold text-[#174C4B]">Analyse Period At:</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => setAnalysisToPeriod(ad.startDate)}
                              className="px-2 py-1 rounded bg-white hover:bg-[#174C4B] text-stone-700 hover:text-white border border-stone-200 text-3xs font-semibold transition-colors shadow-2xs"
                            >
                              Start ({formatDate(ad.startDate)})
                            </button>
                            <button
                              onClick={() => setAnalysisToPeriod(ad.endDate)}
                              className="px-2 py-1 rounded bg-white hover:bg-[#174C4B] text-stone-700 hover:text-white border border-stone-200 text-3xs font-semibold transition-colors shadow-2xs"
                            >
                              End ({formatDate(ad.endDate)})
                            </button>
                            <button
                              onClick={() => handleMomentChange(new Date())}
                              className="px-2 py-1 rounded bg-[#B58A4A]/20 hover:bg-[#B58A4A]/40 text-[#8E692B] border border-[#B58A4A]/40 text-3xs font-bold transition-colors shadow-2xs"
                            >
                              Now
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {activeLevel === 'PD' && allPDs.map(pd => {
                const isCurrent = pd.planet === currentPD?.planet;
                return (
                  <div 
                    key={pd.planet}
                    className={`rounded-xl border p-2.5 flex items-center justify-between transition-all ${
                      isCurrent
                        ? 'bg-amber-50/40 border-[#B58A4A] ring-1 ring-[#B58A4A]/30'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-stone-100 flex items-center justify-center font-bold text-3xs text-[#174C4B]">
                        {pd.planet.slice(0, 2)}
                      </span>
                      <div>
                        <div className="font-bold text-xs text-[#174C4B] flex items-center gap-1.5">
                          <span>{pd.planet} Pratyantar</span>
                          {isCurrent && <span className="text-3xs font-bold px-1 rounded bg-[#174C4B] text-white">ACTIVE</span>}
                        </div>
                        <div className="text-3xs text-stone-500 font-mono">
                          {formatDate(pd.startDate)} → {formatDate(pd.endDate)} ({pd.durationDays}d)
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setAnalysisToPeriod(pd.startDate)}
                      className="px-2 py-1 rounded bg-stone-50 hover:bg-[#174C4B] text-stone-700 hover:text-white border border-stone-200 text-3xs font-semibold transition-colors"
                    >
                      Analyse
                    </button>
                  </div>
                );
              })}

              {activeLevel === 'SD' && allSDs.map(sd => {
                const isCurrent = sd.planet === currentSD?.planet;
                return (
                  <div 
                    key={sd.planet}
                    className={`rounded-xl border p-2.5 flex items-center justify-between transition-all ${
                      isCurrent
                        ? 'bg-amber-50/40 border-[#B58A4A] ring-1 ring-[#B58A4A]/30'
                        : 'bg-white border-stone-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-stone-100 flex items-center justify-center font-bold text-3xs text-[#174C4B]">
                        {sd.planet.slice(0, 2)}
                      </span>
                      <div>
                        <div className="font-bold text-xs text-[#174C4B] flex items-center gap-1.5">
                          <span>{sd.planet} Sookshma</span>
                          {isCurrent && <span className="text-3xs font-bold px-1 rounded bg-[#174C4B] text-white">ACTIVE</span>}
                        </div>
                        <div className="text-3xs text-stone-500 font-mono">
                          {formatDate(sd.startDate)} → {formatDate(sd.endDate)}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setAnalysisToPeriod(sd.startDate)}
                      className="px-2 py-0.5 rounded bg-stone-50 hover:bg-[#174C4B] text-stone-700 hover:text-white border border-stone-200 text-3xs font-semibold transition-colors"
                    >
                      Analyse
                    </button>
                  </div>
                );
              })}

              {activeLevel === 'PrD' && allPrDs.map(prd => {
                const isCurrent = prd.planet === currentPrD?.planet;
                return (
                  <div 
                    key={prd.planet}
                    className={`rounded-xl border p-2.5 flex items-center justify-between transition-all ${
                      isCurrent
                        ? 'bg-amber-50/40 border-[#B58A4A] ring-1 ring-[#B58A4A]/30'
                        : 'bg-white border-stone-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-stone-100 flex items-center justify-center font-bold text-3xs text-[#174C4B]">
                        {prd.planet.slice(0, 2)}
                      </span>
                      <div>
                        <div className="font-bold text-xs text-[#174C4B] flex items-center gap-1.5">
                          <span>{prd.planet} Prana</span>
                          {isCurrent && <span className="text-3xs font-bold px-1 rounded bg-[#174C4B] text-white">ACTIVE</span>}
                        </div>
                        <div className="text-3xs text-stone-500 font-mono">
                          {formatDate(prd.startDate)} → {formatDate(prd.endDate)}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setAnalysisToPeriod(prd.startDate)}
                      className="px-2 py-0.5 rounded bg-stone-50 hover:bg-[#174C4B] text-stone-700 hover:text-white border border-stone-200 text-3xs font-semibold transition-colors"
                    >
                      Analyse
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3. Evidence & Provenance Drawer */}
          <div className={mobileTab === 'evidence' || mobileTab === 'timeline' ? 'block' : 'hidden lg:block'}>
            <EvidenceProvenanceDrawer
              kundali={kundali}
              lang={lang}
              activeMD={currentMD}
              activeAD={currentAD}
              activePDPlanet={currentPD?.planet}
              targetAnalysisDateTime={targetMoment}
            />
          </div>

          {/* 4. Shani Sade Sati Timeline */}
          <ShaniSadeSatiTimeline
            kundali={kundali}
            lang={lang}
          />
        </div>

        {/* RIGHT COLUMN: Dual Chart Context & Gochar Quick-Glance (~40% / col-span-5) */}
        <div className={`lg:col-span-5 space-y-5 ${mobileTab !== 'charts' && mobileTab !== 'gochar' ? 'hidden lg:block' : ''}`}>
          {/* Dual Chart Context Panel */}
          <div className={mobileTab === 'charts' || mobileTab === 'timeline' ? 'block' : 'hidden lg:block'}>
            <DualChartContextPanel
              kundali={kundali}
              lang={lang}
              highlightedPlanet={highlightedPlanet}
              onSelectPlanet={(planetName) => setHighlightedPlanet(planetName)}
            />
          </div>

          {/* Gochar Quick Glance Panel */}
          <div className={mobileTab === 'gochar' || mobileTab === 'timeline' ? 'block' : 'hidden lg:block'}>
            <GocharQuickGlance
              kundali={kundali}
              lang={lang}
              targetAnalysisDateTime={targetMoment}
              onNavigateToGochar={onNavigateToGochar}
              highlightedPlanet={highlightedPlanet}
              onSelectPlanet={(planetName) => setHighlightedPlanet(planetName)}
            />
          </div>
        </div>
      </div>

      {/* Internal BTR Drawer Modal */}
      {isBtrDrawerOpen && (
        <BtrDrawer
          isOpen={isBtrDrawerOpen}
          onClose={() => setIsBtrDrawerOpen(false)}
          kundali={kundali}
          lang={lang}
        />
      )}
    </div>
  );
};
